import { Component, EventEmitter, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonService } from '../../../services/common.service';
import { DriverImportService } from 'src/app/services/driver-import.service';

import { interval, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DownloadFileType, DriverImportGridItem as DriverImportJob } from '../driver-import-interfaces';
import { HttpResponse } from '@angular/common/http';

@Component({
  selector: 'vrm-import-detail-dialog',
  templateUrl: './import-detail-dialog.component.html',
  styleUrls: ['./import-detail-dialog.component.scss'],
})
export class ImportDetailDialogComponent implements OnDestroy, OnInit {
  file: File;
  jobId: string;
  details: any;
  jobStatus: string;
  importedBy: string;
  completedTime: string;
  jobCreatedAt: string;
  processedRecords: number;
  processedErrorCount: number;
  approvedBy: string;
  approvedAt: string;
  rejectedBy: string;
  rejectedAt: string;
  filesToDownload: any = [];
  existingUsersSnapshotCount: number;
  usersToBeCreatedCount: number;
  usersToBeUpdatedCount: number;
  usersToBeDeletedCount: number;
  validationErrorsCount: number;
  validationErrorsFile: string;
  totalRecordsCount: number;
  userId: number;

  percentProgress = { value: 0 };
  processingErrors: any[] = [];
  showProgressBar: boolean = false;
  dialogTitle: string = this.translate.instant('DRIVER_IMPORT.importDetails');
  titleDescription?: string;
  processError: boolean = false;
  jobDetailSubscription: Subscription | null = null;
  errorMessage: string = '';
  onError: boolean = false;
  headerTextColour: string = '';
  isLarge: boolean = false;
  companyColour: string = this.cService.userProfile ? `#${this.cService.userProfile.HqColour}` : '';
  headerBgColour: string = '#028ed3';
  dialogSettings: any = { width: '600px', disableClose: true };
  hasHeaderRow: boolean = true;
  companyName: string;
  showLoader: boolean = true;
  updatedChangePercentage: number = 0;
  createdChangePercentage: number = 0;
  deletedChangePercentage: number = 0;
  colorChangeThreshold: number = 20;
  completedAt;
  downloadFileType = DownloadFileType;
  callBack: () => void;
  constructor(
    public cService: CommonService,
    public dialogRef: MatDialogRef<ImportDetailDialogComponent>,
    private driverImportService: DriverImportService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.userId = this.cService.userProfile?.userId;
    this.companyName = this.cService.userProfile?.company;
    this.callBack = data.callBack;
    this.jobId = data.details.jobId;
    this.completedAt = data.details.completedTime || data.details.completedAt;
    this.details = data.details;
  }

  ngOnDestroy(): void {
    this.stopJobDetailUpdates();
  }

  ngOnInit(): void {
    this.getJobDetail(this.companyName, this.jobId);
    if (this.details.jobStatus === 'COMPLETED_WITH_PROVISION_ERROR') {
      this.getErrors();
    }
    const loop = interval(5 * 1000);
    this.jobDetailSubscription = loop.subscribe(() => {
      this.getJobDetail(this.companyName, this.jobId);
    });
  }

  getJobDetail(companyName: string, jobId: string) {
    this.driverImportService
      .getJobDetails(companyName, jobId)
      .toPromise()
      .then(async (response) => {
        if (response.bodyResponse) {
          this.setJobDetailUi(response.bodyResponse);
        }
        this.percentProgress.value = 0;
        if (response.bodyResponse.jobStatus === 'COMPLETED_WITH_PROVISION_ERROR') {
          this.getErrors();
        }
      });
  }

  stopJobDetailUpdates() {
    if (this.jobDetailSubscription) {
      this.jobDetailSubscription.unsubscribe();
      this.jobDetailSubscription = null;
    }
  }

  private setJobDetailUi(details: DriverImportJob) {
    this.filesToDownload = [];
    const safeNumber = (value: any): number => (typeof value === 'number' ? value : 0);
    this.details = details;
    this.jobId = details.jobId;
    this.completedTime = details.completedTime;
    this.importedBy = details.importedBy;

    for (let key in details.fileEndpoint) {
      if (details.fileEndpoint[key]) {
        this.filesToDownload.push({
          key,
          path: details.fileEndpoint[key],
          type: details.fileEndpoint[key].split('.')[1],
        });
      }
    }

    this.validationErrorsFile = details.fileEndpoint.validationErrors;
    this.validationErrorsCount = safeNumber(details.statistics.validationErrorsCount);
    this.jobCreatedAt = details.createdAt;
    this.approvedBy = details.approvedBy;
    this.approvedAt = details.approvedAt;
    this.rejectedBy = details.rejectedBy;
    this.rejectedAt = details.rejectedAt;

    this.existingUsersSnapshotCount = safeNumber(details.statistics.existingUsersSnapshotCount);
    this.usersToBeCreatedCount = safeNumber(details.statistics.usersToBeCreatedCount);
    this.usersToBeUpdatedCount = safeNumber(details.statistics.usersToBeUpdatedCount);
    this.usersToBeDeletedCount = safeNumber(details.statistics.usersToBeDeletedCount);

    this.totalRecordsCount = this.usersToBeCreatedCount + this.usersToBeUpdatedCount + this.usersToBeDeletedCount;

    this.processedRecords =
      safeNumber(details.statistics.createProcessed) +
      safeNumber(details.statistics.updateProcessed) +
      safeNumber(details.statistics.deleteProcessed);

    this.processedErrorCount =
      safeNumber(details.statistics.createFailed) +
      safeNumber(details.statistics.updateFailed) +
      safeNumber(details.statistics.deleteFailed);

    this.jobStatus = details.jobStatus;
    this.updatedChangePercentage = this.existingUsersSnapshotCount
      ? (this.usersToBeUpdatedCount / this.existingUsersSnapshotCount) * 100
      : 0;

    this.createdChangePercentage = this.existingUsersSnapshotCount
      ? (this.usersToBeCreatedCount / this.existingUsersSnapshotCount) * 100
      : 0;

    this.deletedChangePercentage = this.existingUsersSnapshotCount
      ? (this.usersToBeDeletedCount / this.existingUsersSnapshotCount) * 100
      : 0;

    this.showLoader = false;
  }

  dialogClosed(): void {
    this.closeDialog();
  }

  approve() {
    this.showProgressBar = true;
    this.percentProgress.value = 0;
    this.driverImportService.approveProcessing(this.companyName, this.jobId, this.userId, false).subscribe({
      next: (event) => {
        if (event instanceof HttpResponse) {
          this.percentProgress.value = 100;
          this.showProgressBar = false;
          this.getJobDetail(this.companyName, this.jobId);
        }
      },
      error: (err) => {
        console.error('Server error:', err);
        this.showProgressBar = false;
        this.processError = true;
      },
    });
  }

  reject() {
    this.showProgressBar = true;
    this.percentProgress.value = 0;
    this.driverImportService.approveProcessing(this.companyName, this.jobId, this.userId, true).subscribe({
      next: (event) => {
        if (event instanceof HttpResponse) {
          this.percentProgress.value = 100;
          this.showProgressBar = false;
          this.getJobDetail(this.companyName, this.jobId);
        }
      },
      error: (err) => {
        console.error('Server error:', err);
        this.showProgressBar = false;
        this.processError = true;
      },
    });
  }

  confirm() {
    this.closeDialog();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  downloadFile(type: string): void {
    const filename = this.details.fileEndpoint[type].split('/').pop();
    this.driverImportService.downloadJobFile(this.companyName, this.jobId, type).subscribe(
      (response) => {
        const file = response.body;
        const contentType = response.headers.get('Content-Type');

        if (file instanceof Blob) {
          if (contentType && contentType.includes('application/octet-stream')) {
            const blobUrl = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'downloaded-file';
            link.click();
            window.URL.revokeObjectURL(blobUrl);
          } else {
            console.error('Unexpected content type:', contentType);
            this.processError = true;
          }
        } else {
          console.error('Invalid file format or response body:', file);
          this.processError = true;
        }
      },
      (error) => {
        console.error('Error fetching job errors:', error);
      }
    );
  }
  getErrors() {
    this.driverImportService.getAllErrors(this.companyName, this.jobId).subscribe(
      (response) => {
        console.error('Errors retrieved:', response.bodyResponse);

        this.processingErrors = response.bodyResponse;
      },
      (error) => {
        console.error('Error fetching job errors:', error);
      }
    );
  }
}
